import { makeAutoObservable, runInAction } from "mobx";
import { customFrameworkService } from "../services/customFramework.service";

export { resolveLogoUrl } from "@/lib/utils";

class CustomFrameworkStore {
  frameworks = [];
  selectedFramework = null;
  disclosures = [];
  loading = false;
  disclosuresLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── Frameworks ─────────────────────────────────────────────────────────────

  fetchFrameworks = async () => {
    this.loading = true;
    try {
      const data = await customFrameworkService.list();
      runInAction(() => {
        this.frameworks = data?.frameworks ?? [];
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  fetchFramework = async (id) => {
    // Staleness guard: if a newer fetchFramework(otherId) starts before this
    // one resolves (e.g. fast navigation between two frameworks), don't let
    // this call's late response overwrite the newer one's data.
    this._frameworkRequestId = id;
    this.loading = true;
    try {
      const data = await customFrameworkService.get(id);
      if (this._frameworkRequestId !== id) return;
      runInAction(() => {
        this.selectedFramework = data?.framework ?? null;
      });
    } finally {
      if (this._frameworkRequestId === id) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  };

  createFramework = async (values) => {
    const data = await customFrameworkService.create(values);
    const framework = data?.framework;
    runInAction(() => {
      if (framework) this.frameworks = [framework, ...this.frameworks];
    });
    return framework;
  };

  updateFramework = async (id, values) => {
    const data = await customFrameworkService.update(id, values);
    const updated = data?.framework;
    runInAction(() => {
      this.frameworks = this.frameworks.map((f) =>
        f._id === id ? updated : f,
      );
      if (this.selectedFramework?._id === id) this.selectedFramework = updated;
    });
    return updated;
  };

  deleteFramework = async (id) => {
    await customFrameworkService.remove(id);
    runInAction(() => {
      this.frameworks = this.frameworks.filter((f) => f._id !== id);
      if (this.selectedFramework?._id === id) this.selectedFramework = null;
    });
  };

  uploadLogo = async (frameworkId, file) => {
    const data = await customFrameworkService.uploadLogo(frameworkId, file);
    const updated = data?.framework;
    if (!updated) return;
    runInAction(() => {
      this.frameworks = this.frameworks.map((f) =>
        f._id === frameworkId ? updated : f,
      );
      if (this.selectedFramework?._id === frameworkId)
        this.selectedFramework = updated;
    });
    return updated;
  };

  // ─── Disclosures ─────────────────────────────────────────────────────────────

  fetchDisclosures = async (frameworkId) => {
    // Same staleness guard as fetchFramework — see comment there.
    this._disclosuresRequestId = frameworkId;
    this.disclosuresLoading = true;
    try {
      const data = await customFrameworkService.listDisclosures(frameworkId);
      if (this._disclosuresRequestId !== frameworkId) return;
      runInAction(() => {
        this.disclosures = data?.disclosures ?? [];
      });
    } finally {
      if (this._disclosuresRequestId === frameworkId) {
        runInAction(() => {
          this.disclosuresLoading = false;
        });
      }
    }
  };

  addDisclosure = async (frameworkId, values) => {
    const data = await customFrameworkService.addDisclosure(
      frameworkId,
      values,
    );
    const disclosure = data?.disclosure;
    runInAction(() => {
      if (disclosure) this.disclosures = [...this.disclosures, disclosure];
    });
    return disclosure;
  };

  updateDisclosure = async (frameworkId, discId, values) => {
    const data = await customFrameworkService.updateDisclosure(
      frameworkId,
      discId,
      values,
    );
    const updated = data?.disclosure;
    runInAction(() => {
      this.disclosures = this.disclosures.map((d) =>
        d._id === discId ? updated : d,
      );
    });
    return updated;
  };

  deleteDisclosure = async (frameworkId, discId) => {
    await customFrameworkService.removeDisclosure(frameworkId, discId);
    runInAction(() => {
      this.disclosures = this.disclosures.filter((d) => d._id !== discId);
    });
  };

  clearDisclosures = () => {
    this.disclosures = [];
    this.selectedFramework = null;
  };
}

export const customFrameworkStore = new CustomFrameworkStore();
